import {useAuditRecorder} from './audit/AuditRecorder';
import { PlatformSmtpPage } from './admin/PlatformSmtpPage';
import { EnrollmentPage, EnrollmentVerificationPage, EnrollmentReviewPage } from "./auth/EnrollmentPages";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { SessionUser } from "@epos/contracts";
import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useEffect } from "react";
import { CustomerPortalPage, CustomerSignupPage } from "./customers/CustomerAccountPages";
import { useAuth } from "./auth/AuthContext";
import { LoginPage } from "./auth/LoginPage";
import { RecoveryPage } from "./auth/RecoveryPage";
import { PosLoginPage } from "./auth/PosLoginPage";
import { hasAnySettingsPermission, hasCrmStaffPermission } from "./modules";
import { LegacyShell } from "./LegacyShell";
import {
    ActivationPage,
} from "./auth/RegistrationPages";
import "./auth/recovery.css";
import { api } from "./api";

const ActivityFormPage = lazy(() => import("./activities/ActivityFormPage").then(module => ({ default: module.ActivityFormPage })));
const PublicBookingPage = lazy(() => import("./public/PublicBookingPage").then(module => ({ default: module.PublicBookingPage })));
const PublicBookingAccessPage = lazy(() => import("./public/PublicBookingAccessPage").then(module => ({ default: module.PublicBookingAccessPage })));
const LegacyWaiverAccessPage = lazy(() => import("./public/PublicBookingAccessPage").then(module => ({ default: module.LegacyWaiverAccessPage })));
const PublicPaymentCheckoutPage = lazy(() => import("./public/PublicPaymentCheckoutPage"));
const PublicPaymentResultPage = lazy(() => import("./public/PublicBookingLifecyclePages").then(module => ({ default: module.PublicPaymentResultPage })));
const PublicReviewPage = lazy(() => import("./public/PublicBookingLifecyclePages").then(module => ({ default: module.PublicReviewPage })));
const PublicBookingCancelPage = lazy(() => import("./public/PublicBookingLifecyclePages").then(module => ({ default: module.PublicBookingCancelPage })));
const LegacyCancelPage = lazy(() => import("./public/PublicBookingLifecyclePages").then(module => ({ default: module.LegacyCancelPage })));
const LegacyBookingStatusPage = lazy(() => import("./public/PublicBookingLifecyclePages").then(module => ({ default: module.LegacyBookingStatusPage })));
const PublicContentPage = lazy(() => import("./public/MarketingPages").then(module => ({ default: module.PublicContentPage })));
const PublicTableOrderPage = lazy(() => import("./public/PublicTableOrderPage").then(module => ({ default: module.PublicTableOrderPage })));
const PublicBookingDetailsPage = lazy(() => import("./public/PublicBookingDetailsPage").then(module => ({ default: module.PublicBookingDetailsPage })));
const PublicReceiptPage = lazy(() => import("./public/PublicReceiptPage").then(module => ({ default: module.PublicReceiptPage })));
const PublicStockPhotoPage = lazy(() => import("./stock/PublicStockPhotoPage").then(module => ({ default: module.PublicStockPhotoPage })));
const CustomersPage = lazy(() => import("./customers/CustomersPage").then(module => ({ default: module.CustomersPage })));
const CustomerFormPage = lazy(() => import("./customers/CustomerFormPage").then(module => ({ default: module.CustomerFormPage })));
const CustomerDetailsPage = lazy(() => import("./customers/CustomerDetailsPage").then(module => ({ default: module.CustomerDetailsPage })));
const LoyaltyPage = lazy(() => import("./customers/LoyaltyPage").then(module => ({ default: module.LoyaltyPage })));
const MembershipPage = lazy(() => import("./customers/MembershipPage").then(module => ({ default: module.MembershipPage })));
const MembershipAllocationPage = lazy(() => import("./customers/MembershipPage").then(module => ({ default: module.MembershipAllocationPage })));
const MarketingPage = lazy(() => import("./customers/MarketingPage").then(module => ({ default: module.MarketingPage })));
const SpecialEventsPage = lazy(() => import("./customers/SpecialEventsPage").then(module => ({ default: module.SpecialEventsPage })));
const ActivityCategoriesPage = lazy(() => import("./activity-categories/ActivityCategoriesPage").then(module => ({ default: module.ActivityCategoriesPage })));
const ActivityCategoryFormPage = lazy(() => import("./activity-categories/ActivityCategoryFormPage").then(module => ({ default: module.ActivityCategoryFormPage })));
const LocationsPage = lazy(() => import("./locations/LocationsPage").then(module => ({ default: module.LocationsPage })));
const LocationFormPage = lazy(() => import("./locations/LocationFormPage").then(module => ({ default: module.LocationFormPage })));
const StockCategoriesPage = lazy(() => import("./stock-categories/StockCategoriesPage").then(module => ({ default: module.StockCategoriesPage })));
const StockCategoryFormPage = lazy(() => import("./stock-categories/StockCategoryFormPage").then(module => ({ default: module.StockCategoryFormPage })));
const ActivitiesPage = lazy(() => import("./activities/ActivitiesPage").then(module => ({ default: module.ActivitiesPage })));
const WidgetBookingUrlsPage = lazy(() => import("./activities/WidgetBookingUrlsPage").then(module => ({ default: module.WidgetBookingUrlsPage })));
const ActivityCalendarPage = lazy(() => import("./activities/ActivityCalendarPage").then(module => ({ default: module.ActivityCalendarPage })));
const MissedSessionPage = lazy(() => import("./bookings/MissedSessionPage").then(module => ({ default: module.MissedSessionPage })));
const BookingFormPage = lazy(() => import("./bookings/BookingFormPage").then(module => ({ default: module.BookingFormPage })));
const BookingsPage = lazy(() => import("./bookings/BookingsPage").then(module => ({ default: module.BookingsPage })));
const BookingDetailsPage = lazy(() => import("./bookings/BookingDetailsPage").then(module => ({ default: module.BookingDetailsPage })));
const BookingSlotEditPage = lazy(() => import("./bookings/BookingSlotEditPage").then(module => ({ default: module.BookingSlotEditPage })));
const PosSaleDetailsPage = lazy(() => import("./bookings/PosSaleDetailsPage").then(module => ({ default: module.PosSaleDetailsPage })));
const CalendarPage = lazy(() => import("./calendar/CalendarPage").then(module => ({ default: module.CalendarPage })));
const AttendeesPage = lazy(() => import("./attendees/AttendeesPage").then(module => ({ default: module.AttendeesPage })));
const AttendeeFormPage = lazy(() => import("./attendees/AttendeeFormPage").then(module => ({ default: module.AttendeeFormPage })));
const WaiverSettingFormPage = lazy(() => import("./waivers/WaiverSettingFormPage").then(module => ({ default: module.WaiverSettingFormPage })));
const WaiverSettingsPage = lazy(() => import("./waivers/WaiverSettingsPage").then(module => ({ default: module.WaiverSettingsPage })));
const SignedWaiverManagementPage = lazy(() => import("./waivers/SignedWaiverManagementPage").then(module => ({ default: module.SignedWaiverManagementPage })));
const SignedWaiverDetailsPage = lazy(() => import("./waivers/SignedWaiverManagementPage").then(module => ({ default: module.SignedWaiverDetailsPage })));
const StockPage = lazy(() => import("./stock/StockPage").then(module => ({ default: module.StockPage })));
const StockFormPage = lazy(() => import("./stock/StockFormPage").then(module => ({ default: module.StockFormPage })));
const StockAutomationPage = lazy(() => import("./stock/StockAutomationPage").then(module => ({ default: module.StockAutomationPage })));
const ReceivePurchaseOrderPage = lazy(() => import("./stock/ReceivePurchaseOrderPage").then(module => ({ default: module.ReceivePurchaseOrderPage })));
const SuppliersPage = lazy(() => import("./suppliers/SupplierPages").then(module => ({ default: module.SuppliersPage })));
const SupplierPage = lazy(() => import("./suppliers/SupplierPages").then(module => ({ default: module.SupplierPage })));
const StockSubCategoriesPage = lazy(() => import("./stock/StockSubCategoriesPage").then(module => ({ default: module.StockSubCategoriesPage })));
const StockSubCategoryFormPage = lazy(() => import("./stock/StockSubCategoryFormPage").then(module => ({ default: module.StockSubCategoryFormPage })));
const InventoryScanPage = lazy(() => import("./stock/InventoryScanPage").then(module => ({ default: module.InventoryScanPage })));
const ManageInventoryPage = lazy(() => import("./stock/ManageInventoryPage").then(module => ({ default: module.ManageInventoryPage })));
const StockTakePage = lazy(() => import("./stock/StockTakePage").then(module => ({ default: module.StockTakePage })));
const StockTakeHistoryPage = lazy(() => import("./stock/StockTakeHistoryPage").then(module => ({ default: module.StockTakeHistoryPage })));
const StockMovementsPage = lazy(() => import("./stock/StockMovementsPage").then(module => ({ default: module.StockMovementsPage })));
const TableLayoutsPage = lazy(() => import("./tables/TableLayoutsPage").then(module => ({ default: module.TableLayoutsPage })));
const TableLayoutEditorPage = lazy(() => import("./tables/TableLayoutEditorPage").then(module => ({ default: module.TableLayoutEditorPage })));
const StaffPage = lazy(() => import("./staff/StaffPage").then(module => ({ default: module.StaffPage })));
const StaffFormPage = lazy(() => import("./staff/StaffFormPage").then(module => ({ default: module.StaffFormPage })));
const StaffPermissionsPage = lazy(() => import("./staff/StaffPermissionsPage").then(module => ({ default: module.StaffPermissionsPage })));
const SettingsPage = lazy(() => import("./settings/SettingsPage").then(module => ({ default: module.SettingsPage })));
const ReportsPage = lazy(() => import("./reports/ReportsPage").then(module => ({ default: module.ReportsPage })));
const ProfilePage = lazy(() => import("./profile/ProfilePage").then(module => ({ default: module.ProfilePage })));
const DashboardPage = lazy(() => import("./dashboard/DashboardPage").then(module => ({ default: module.DashboardPage })));
const PaymentsPage = lazy(() => import("./finance/PaymentsPage").then(module => ({ default: module.PaymentsPage })));
const RefundsPage = lazy(() => import("./finance/RefundsPage").then(module => ({ default: module.RefundsPage })));
const InvoicesPage = lazy(() => import("./finance/InvoicesPage").then(module => ({ default: module.InvoicesPage })));
const PaymentGatewayPage = lazy(() => import("./finance/PaymentGatewayPage").then(module => ({ default: module.PaymentGatewayPage })));
const InvoiceViewPage = lazy(() => import("./finance/InvoiceViewPage").then(module => ({ default: module.InvoiceViewPage })));
const CreditNoteViewPage = lazy(() => import("./finance/CreditNoteViewPage").then(module => ({ default: module.CreditNoteViewPage })));
const ReviewsPage = lazy(() => import("./finance/ReviewsPage").then(module => ({ default: module.ReviewsPage })));
const PrinterLogPage = lazy(() => import("./printers/PrinterLogPage").then(module => ({ default: module.PrinterLogPage })));
const DevicesModule = () => import("./operations/DevicesPage");
const PrintersPage = lazy(() => DevicesModule().then(module => ({ default: module.PrintersPage })));
const TerminalsPage = lazy(() => DevicesModule().then(module => ({ default: module.TerminalsPage })));
const CrmSubscriptionPage = lazy(() => import("./subscriptions/SubscriptionPages").then(module => ({ default: module.CrmSubscriptionPage })));
const DirectDebitCompletePage = lazy(() => import("./subscriptions/SubscriptionPages").then(module => ({ default: module.DirectDebitCompletePage })));
const ClientSubscriptionSetupPage = lazy(() => import("./subscriptions/SubscriptionPages").then(module => ({ default: module.ClientSubscriptionSetupPage })));
const SubscriptionRequestsPage = lazy(() => import("./subscriptions/SubscriptionPages").then(module => ({ default: module.SubscriptionRequestsPage })));
const SubscriptionRequestDetailsPage = lazy(() => import("./subscriptions/SubscriptionPages").then(module => ({ default: module.SubscriptionRequestDetailsPage })));
const ClientsPage = lazy(() => import("./admin/AdminPages").then(module => ({ default: module.ClientsPage })));
const ClientFormPage = lazy(() => import("./admin/AdminPages").then(module => ({ default: module.ClientFormPage })));
const ClientDetailsPage = lazy(() => import("./admin/AdminPages").then(module => ({ default: module.ClientDetailsPage })));
const SuperAdminStaffPage = lazy(() => import("./admin/SuperAdminStaffPages").then(module => ({ default: module.SuperAdminStaffPage })));
const SuperAdminStaffFormPage = lazy(() => import("./admin/SuperAdminStaffPages").then(module => ({ default: module.SuperAdminStaffFormPage })));
const SuperAdminStaffDetailsPage = lazy(() => import("./admin/SuperAdminStaffPages").then(module => ({ default: module.SuperAdminStaffDetailsPage })));
const SuperAdminStaffPermissionsPage = lazy(() => import("./admin/SuperAdminStaffPages").then(module => ({ default: module.SuperAdminStaffPermissionsPage })));
const InquiriesPage = lazy(() => import("./admin/InquiriesPage").then(module => ({ default: module.InquiriesPage })));
const InquiryDetailsPage = lazy(() => import("./admin/InquiriesPage").then(module => ({ default: module.InquiryDetailsPage })));
const EmailTemplatesPage = lazy(() => import("./admin/EmailTemplatePages").then(module => ({ default: module.EmailTemplatesPage })));
const CustomerEmailTemplatesPage = lazy(() => import("./customers/CustomerEmailTemplatesPage").then(module => ({ default: module.CustomerEmailTemplatesPage })));
const EmailTemplateFormPage = lazy(() => import("./admin/EmailTemplatePages").then(module => ({ default: module.EmailTemplateFormPage })));
const EmailTemplateDetailsPage = lazy(() => import("./admin/EmailTemplatePages").then(module => ({ default: module.EmailTemplateDetailsPage })));
const InvoiceLayoutPage = lazy(() => import("./admin/EmailTemplatePages").then(module => ({ default: module.InvoiceLayoutPage })));
const AdminInvoicesPage = lazy(() => import("./admin/AdminInvoicePages").then(module => ({ default: module.AdminInvoicesPage })));
const AdminInvoiceDetailsPage = lazy(() => import("./admin/AdminInvoicePages").then(module => ({ default: module.AdminInvoiceDetailsPage })));
const AdminChargePage = lazy(() => import("./admin/AdminChargePage").then(module => ({ default: module.AdminChargePage })));
const WebsiteSettingsPage = lazy(() => import("./admin/WebsiteSettingsPage").then(module => ({ default: module.WebsiteSettingsPage })));
const PlatformAiSettingsPage = lazy(() => import("./admin/PlatformAiSettingsPage").then(module => ({ default: module.PlatformAiSettingsPage })));
const ContentPagesPage = lazy(() => import("./admin/ContentPages").then(module => ({ default: module.ContentPagesPage })));
const ContentPageFormPage = lazy(() => import("./admin/ContentPages").then(module => ({ default: module.ContentPageFormPage })));
const ContentPageDetailsPage = lazy(() => import("./admin/ContentPages").then(module => ({ default: module.ContentPageDetailsPage })));
const SuperAdminSecondaryPage = lazy(() => import("./admin/SuperAdminSecondaryPages").then(module => ({ default: module.SuperAdminSecondaryPage })));
const PosPage = lazy(() => import("./operations/PosPage").then(module => ({ default: module.PosPage })));
const KdsPage = lazy(() => import("./operations/KdsPage").then(module => ({ default: module.KdsPage })));
const KioskPage = lazy(() => import("./operations/KioskPage").then(module => ({ default: module.KioskPage })));
const DeviceHardwareSettingsPage = lazy(() => import("./operations/DeviceHardwareSettings").then(module => ({ default: module.DeviceHardwareSettingsPage })));
const SupportTicketsPage = lazy(() => import("./admin/SupportPages").then(module => ({ default: module.SupportTicketsPage })));
const SupportSessionsPage = lazy(() => import("./admin/SupportPages").then(module => ({ default: module.SupportSessionsPage })));
const DevelopersPage = lazy(() => import("./admin/DevelopersPage").then(module => ({ default: module.DevelopersPage })));

const moduleFallback = <div className="boot-screen">Loading selected module…</div>;

const protectedRoots = new Set([
    "audit",
    "ai-support",
    "home",
    "customers",
    "activity-categories",
    "locations",
    "stock-categories",
    "activities",
    "orders",
    "calendar",
    "attendee",
    "waivers",
    "waiver-settings",
    "waiver-management",
    "stock",
    "tables",
    "staff",
    "settings",
    "profile",
    "subscription",
    "payments",
    "refunds",
    "invoices",
    "payment-gateway",
    "reports",
    "printer-log",
    "reviews",
    "pos",
    "kiosk",
    "kds",
    "terminals",
    "printers",
]);
export function canAccessPath(user: SessionUser, path: string) {
    if (path.startsWith("/customers/email-templates")) return user.userType === 2;
    if (path.startsWith("/profile")) return true;
    if (path === "/" || path === "/home") {
        if (user.userType === 4 || user.userType === 5)
            return user.permissions.includes("*") || user.permissions.includes("/");
        return true;
    }
    if (user.userType === 3) return path.startsWith("/admin/");
    if (user.userType === 4) {
        if (!path.startsWith("/admin/")) return false;
        if (user.permissions.includes("*")) return true;
        if (
            path.startsWith("/admin/subscription-requests") ||
            path.startsWith("/admin/user-subscriptions")
        )
            return user.permissions.includes("/admin/subscriptions");
        return user.permissions.some(
            (permission) => permission !== "/" && path.startsWith(permission),
        );
    }
    if (user.userType === 2)
        return (
            !path.startsWith("/admin/") &&
            !["/pos", "/kiosk", "/kds"].some(
                (operationalPath) =>
                    path === operationalPath ||
                    path.startsWith(`${operationalPath}/`),
            )
        );
    if (path.startsWith("/audit") || path.startsWith("/ai-support")) return false;
    if (user.userType !== 5) return false;
    if (user.permissions.includes("*"))
        return !path.startsWith("/admin/") && !path.startsWith("/staff");
    const has = (permission: string) => hasCrmStaffPermission(user.permissions, permission);
    if (path.startsWith("/settings")) return hasAnySettingsPermission(user.permissions);
    if (path.startsWith("/terminals") || path.startsWith("/printers")) return has("/settings/devices");
    if (path.startsWith("/printer-log")) return has("/printer-log");
    if (path.startsWith("/activities"))
        return has("/activities");
    if (path.startsWith("/activity-categories"))
        return has("/activity-categories");
    if (path.startsWith("/locations"))
        return has("/locations");
    if (path.startsWith("/stock-categories"))
        return has("/stock-categories");
    if (path.startsWith("/stock/inventory") || path.startsWith("/stock/automation") || path.startsWith("/stock/scan") || path.startsWith("/suppliers") || path.startsWith("/purchase-orders")) return has("/stock/inventory");
    if (path.startsWith("/stock")) return has("/stock/items");
    if (path.startsWith("/orders")) return has("/orders");
    if (path.startsWith("/calendar") || path.startsWith("/attendee")) return has("/orders");
    if (path.startsWith("/customers/loyalty")) return has("/customers/loyalty");
    if (path.startsWith("/customers/membership")) return has("/customers/membership");
    if (path.startsWith("/customers/marketing")) return has("/customers/marketing");
    if (path.startsWith("/customers/special-events")) return has("/customers/special-events");
    if (path.startsWith("/customers")) return has("/customers/all");
    if (path.startsWith("/payments"))
        return has("/payments/history");
    if (path.startsWith("/refunds"))
        return has("/refunds");
    if (path.startsWith("/invoices"))
        return (
            has("/invoices")
        );
    if (path.startsWith("/reviews"))
        return has("/reviews");
    if (path.startsWith("/waiver-management"))
        return has("/waiver-management");
    if (path.startsWith("/waiver-settings"))
        return has("/waiver-settings");
    if (path === "/waivers")
        return has("/waiver-settings");
    if (path.startsWith("/waivers/settings"))
        return has("/waiver-settings");
    if (path.startsWith("/pos")) return has("/pos");
    if (path.startsWith("/tables")) return has("/tables");
    if (path.startsWith("/kiosk")) return has("/kiosk/self-service");
    if (path.startsWith("/kds")) return has("/kds");
    if (path.startsWith("/reports")) return has("/reports");
    if (path.startsWith("/payment-gateway")) return has("/payment-gateway");
    return false;
}
function AccessDenied() {
    return (
        <section className="legacy-page">
            <div className="legacy-content-header">
                <h1>403</h1>
                <ol className="legacy-breadcrumb">
                    <li>
                        <Link to="/">Home</Link>
                    </li>
                    <li>Access Denied</li>
                </ol>
            </div>
            <div className="legacy-data-card">
                <div className="legacy-card-body">
                    <h2>Unauthorized action.</h2>
                    <p>You do not have permission to access this page.</p>
                    <Link className="legacy-info-button" to="/">
                        Return to Dashboard
                    </Link>
                </div>
            </div>
        </section>
    );
}
function NotFound() {
    return (
        <section className="legacy-page">
            <div className="legacy-content-header">
                <h1>404</h1>
                <ol className="legacy-breadcrumb">
                    <li>
                        <Link to="/">Home</Link>
                    </li>
                    <li>Page Not Found</li>
                </ol>
            </div>
            <div className="legacy-data-card">
                <div className="legacy-card-body">
                    <h2>Page not found.</h2>
                    <p>The requested page does not exist.</p>
                    <Link className="legacy-info-button" to="/">
                        Return to Dashboard
                    </Link>
                </div>
            </div>
        </section>
    );
}

const AuditPage=lazy(()=>import("./audit/AuditPage").then(module=>({default:module.AuditPage})));
const AiSupportPage=lazy(()=>import("./support/AiSupportPage").then(module=>({default:module.AiSupportPage})));

export function App() {
    useAuditRecorder();
    const { user, loading, logout } = useAuth();
    const location = useLocation();
    useEffect(() => {
        const collapse = (event: MouseEvent) => {
            const button = (
                event.target as HTMLElement
            ).closest<HTMLButtonElement>(".legacy-form-card-header button");
            if (!button) return;
            const icon = button.querySelector<HTMLElement>(
                ".fa-minus, .fa-plus",
            );
            if (!icon) return;
            const body = button.closest(".legacy-form-card-header")
                ?.nextElementSibling as HTMLElement | null;
            if (!body) return;
            body.hidden = !body.hidden;
            button.setAttribute("aria-expanded", String(!body.hidden));
            icon.classList.toggle("fa-minus", !body.hidden);
            icon.classList.toggle("fa-plus", body.hidden);
        };
        document.addEventListener("click", collapse);
        return () => document.removeEventListener("click", collapse);
    }, []);
    const legacyWidget = location.pathname.match(
            /^\/([^/]+)\/(booking-details|booking)\/?$/,
        ),
        legacyWidgetCancel = location.pathname.match(
            /^\/([^/]+)\/booking-cancel\/?$/,
        ),
        legacyPosLogin = location.pathname.match(/^\/([^/]+)\/pos\/login\/?$/),
        legacyPos = location.pathname.match(
            /^\/([^/]+)\/(pos|kiosk|kds-details)\/?$/,
        );
    const publicSlug =
        location.pathname.split("/").filter(Boolean).length === 1
            ? location.pathname.slice(1)
            : "";
    const publicPages = useQuery({
        queryKey: ["public-pages"],
        queryFn: api.publicPages,
        enabled: !loading && !user && Boolean(publicSlug),
    });
    if (location.pathname.startsWith("/stock-photo/"))
        return <Suspense fallback={moduleFallback}><PublicStockPhotoPage token={decodeURIComponent(location.pathname.split("/")[2] || "")} /></Suspense>;
    if (location.pathname.startsWith("/book/"))
        return (
            <PublicBookingPage
                subdomain={location.pathname.split("/")[2] || "demo"}
            />
        );
    if (legacyPosLogin)
        return (
            <PosLoginPage subdomain={decodeURIComponent(legacyPosLogin[1])} />
        );
    if (legacyPos && !user)
        return <PosLoginPage subdomain={decodeURIComponent(legacyPos[1])} />;
    if (legacyPos && user)
        return (
            <Navigate
                to={
                    legacyPos[2] === "kiosk"
                        ? "/kiosk"
                        : legacyPos[2] === "kds-details"
                          ? "/kds"
                          : "/pos"
                }
                replace
            />
        );
    if (legacyWidget)
        return (
            <PublicBookingPage
                subdomain={decodeURIComponent(legacyWidget[1])}
            />
        );
    if (location.pathname === "/booking/payment/checkout") return <PublicPaymentCheckoutPage sessionToken={new URLSearchParams(location.search).get("session") || ""}/>;
    if (legacyWidgetCancel)
        return (
            <PublicPaymentResultPage
                result="cancel"
                token={new URLSearchParams(location.search).get("token") || ""}
                sessionToken={new URLSearchParams(location.search).get("session") || ""}
                bookingSubdomain={new URLSearchParams(location.search).get("subdomain") || ""}
            />
        );
    if (location.pathname.startsWith("/table-order/"))
        return (
            <PublicTableOrderPage
                token={decodeURIComponent(
                    location.pathname.split("/")[2] || "",
                )}
            />
        );
    if (
        location.pathname === "/booking/payment/success/" ||
        location.pathname === "/booking/payment/success"
    )
        return (
            <PublicPaymentResultPage
                result="success"
                token={new URLSearchParams(location.search).get("token") || ""}
                sessionToken={new URLSearchParams(location.search).get("session") || ""}
                bookingSubdomain={new URLSearchParams(location.search).get("subdomain") || ""}
            />
        );
    if (
        location.pathname === "/booking/payment/cancel/" ||
        location.pathname === "/booking/payment/cancel"
    )
        return (
            <PublicPaymentResultPage
                result="cancel"
                token={new URLSearchParams(location.search).get("token") || ""}
                sessionToken={new URLSearchParams(location.search).get("session") || ""}
                bookingSubdomain={new URLSearchParams(location.search).get("subdomain") || ""}
            />
        );
    if (location.pathname.startsWith("/public/review/"))
        return (
            <PublicReviewPage token={location.pathname.split("/")[3] || ""} />
        );
    if (location.pathname.startsWith("/review/"))
        return (
            <PublicReviewPage token={location.pathname.split("/")[3] || ""} />
        );
    if (location.pathname.startsWith("/public/cancel/"))
        return (
            <PublicBookingCancelPage
                token={location.pathname.split("/")[3] || ""}
            />
        );
    if (location.pathname.startsWith("/public/booking/"))
        return (
            <PublicBookingAccessPage
                token={location.pathname.split("/")[3] || ""}
            />
        );
    if (location.pathname.startsWith("/booking/waver/"))
        return (
            <LegacyWaiverAccessPage
                bookingNumber={decodeURIComponent(
                    location.pathname.split("/")[3] || "",
                )}
                bookingId={decodeURIComponent(
                    location.pathname.split("/")[4] || "",
                )}
            />
        );
    if (location.pathname.startsWith("/managebooking/waver/"))
        return (
            <LegacyWaiverAccessPage
                bookingNumber={decodeURIComponent(
                    location.pathname.split("/")[3] || "",
                )}
                bookingId={decodeURIComponent(
                    location.pathname.split("/")[4] || "",
                )}
            />
        );
    if (location.pathname.startsWith("/booking/details/success/"))
        return (
            <PublicBookingDetailsPage
                bookingNumber={decodeURIComponent(
                    location.pathname.split("/")[4] || "",
                )}
                bookingId={decodeURIComponent(
                    location.pathname.split("/")[5] || "",
                )}
            />
        );
    if (location.pathname.startsWith("/alert/link/"))
        return (
            <LegacyCancelPage
                bookingNumber={decodeURIComponent(
                    location.pathname.split("/")[3] || "",
                )}
                bookingId={decodeURIComponent(
                    location.pathname.split("/")[4] || "",
                )}
            />
        );
    if (location.pathname.startsWith("/cancel/booking/"))
        return (
            <LegacyCancelPage
                bookingNumber={decodeURIComponent(
                    location.pathname.split("/")[3] || "",
                )}
                bookingId={decodeURIComponent(
                    location.pathname.split("/")[4] || "",
                )}
                autoCancel
            />
        );
    if (location.pathname.startsWith("/update-booking-status/"))
        return (
            <LegacyBookingStatusPage
                bookingNumber={decodeURIComponent(
                    location.pathname.split("/")[2] || "",
                )}
                bookingDate={decodeURIComponent(
                    location.pathname.split("/")[3] || "",
                )}
                slotTime={decodeURIComponent(
                    location.pathname.split("/")[4] || "",
                )}
            />
        );
    if (location.pathname === "/receipt" || location.pathname === "/print")
        return (
            <PublicReceiptPage
                orderId={
                    new URLSearchParams(location.search).get("order_id") || ""
                }
            />
        );
    if (location.pathname.startsWith("/page/"))
        return (
            <PublicContentPage slug={location.pathname.split("/")[2] || ""} />
        );
    if (location.pathname === "/register") return <EnrollmentPage />;
    if (location.pathname === "/enrolment/verify") return <EnrollmentVerificationPage />;
    if (["/contact-us"].includes(location.pathname))
        return <Navigate to={user ? "/home" : "/login"} replace />;
    if (location.pathname.startsWith("/activate/"))
        return (
            <ActivationPage
                userId={Number(location.pathname.split("/")[2])}
                token={location.pathname.split("/")[3] || ""}
            />
        );
    if (location.pathname.startsWith("/customer/signup/"))
        return <CustomerSignupPage token={location.pathname.split("/")[3] || ""} />;
    if (location.pathname.startsWith("/user/activation/"))
        return (
            <ActivationPage
                userId={Number(location.pathname.split("/")[3])}
                token={location.pathname.split("/")[4] || ""}
            />
        );
    if (loading)
        return <div className="boot-screen">Loading Epos Bookings…</div>;
    if (!user && location.pathname === "/forgot-password")
        return <RecoveryPage />;
    if (!user && location.pathname === "/")
        return <Navigate to="/login" replace />;
    if (!user && location.pathname === "/login") return <LoginPage />;
    if (!user && publicSlug && publicPages.isLoading)
        return <div className="boot-screen">Loading Epos Bookings...</div>;
    if (
        !user &&
        publicPages.data?.some(
            (page) => page.slug.replace(/^\//, "") === publicSlug,
        )
    )
        return <PublicContentPage slug={publicSlug} />;
    if (!user && publicSlug && !protectedRoots.has(publicSlug))
        return <PublicContentPage slug={publicSlug} />;
    if (!user) return <LoginPage />;
    if (user.userType === 1) return <CustomerPortalPage />;
    if (location.pathname === "/device-settings") return <Suspense fallback={moduleFallback}><DeviceHardwareSettingsPage /></Suspense>;
    if (!canAccessPath(user, location.pathname))
        return (
            <LegacyShell user={user} logout={logout}>
                <AccessDenied />
            </LegacyShell>
        );
    if (location.pathname === "/pos") return <Suspense fallback={moduleFallback}><PosPage /></Suspense>;
    if (location.pathname === "/kiosk") return <Suspense fallback={moduleFallback}><KioskPage /></Suspense>;
    if (location.pathname === "/kds") return <Suspense fallback={moduleFallback}><KdsPage /></Suspense>;
    return (
        <LegacyShell user={user} logout={logout}>
            <Suspense fallback={moduleFallback}>
            <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/home" element={<DashboardPage />} />
                <Route path="/admin/enrolments" element={<EnrollmentReviewPage />} />
                <Route path="/customers" element={<CustomersPage />} />
                <Route path="/customers/marketing" element={<MarketingPage />} />
                <Route path="/customers/email-templates" element={<CustomerEmailTemplatesPage />} />
                <Route path="/customers/special-events" element={<SpecialEventsPage />} />
                <Route path="/customers/loyalty" element={<LoyaltyPage />} />
                <Route path="/customers/membership" element={<MembershipPage />} />
                <Route path="/customers/membership/allocate" element={<MembershipAllocationPage />} />
                <Route path="/customers/new" element={<CustomerFormPage />} />
                <Route
                    path="/customers/:id"
                    element={<CustomerDetailsPage />}
                />
                <Route
                    path="/customers/:id/edit"
                    element={<CustomerFormPage />}
                />
                <Route
                    path="/activity-categories"
                    element={<ActivityCategoriesPage />}
                />
                <Route
                    path="/activity-categories/new"
                    element={<ActivityCategoryFormPage />}
                />
                <Route
                    path="/activity-categories/:id/edit"
                    element={<ActivityCategoryFormPage />}
                />
                <Route path="/locations" element={<LocationsPage />} />
                <Route path="/locations/new" element={<LocationFormPage />} />
                <Route
                    path="/locations/:id/edit"
                    element={<LocationFormPage />}
                />
                <Route
                    path="/stock-categories"
                    element={<StockCategoriesPage />}
                />
                <Route
                    path="/stock-categories/new"
                    element={<StockCategoryFormPage />}
                />
                <Route
                    path="/stock-categories/:id/edit"
                    element={<StockCategoryFormPage />}
                />
                <Route path="/activities" element={<ActivitiesPage />} />
                <Route path="/activities/widget-booking-urls" element={<WidgetBookingUrlsPage />} />
                <Route path="/activities/new" element={<ActivityFormPage />} />
                <Route
                    path="/activities/:id"
                    element={<ActivityFormPage view />}
                />
                <Route
                    path="/activities/:id/edit"
                    element={<ActivityFormPage />}
                />
                <Route
                    path="/activities/:id/calendar"
                    element={<ActivityCalendarPage />}
                />
                <Route path="/audit" element={<AuditPage />} />
                <Route path="/ai-support" element={<AiSupportPage />} />
                <Route path="/orders" element={<BookingsPage />} />
                <Route path="/orders/:id" element={<BookingDetailsPage />} />
                <Route path="/orders/pos/:id" element={<PosSaleDetailsPage />} />
                <Route path="/orders/:id/edit" element={<BookingFormPage />} />
                <Route path="/orders/:id/missed/:slotId" element={<MissedSessionPage />} />
                <Route
                    path="/orders/:id/slots/:slotId/edit"
                    element={<BookingSlotEditPage />}
                />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/attendee" element={<AttendeesPage />} />
                <Route path="/attendee/create" element={<AttendeeFormPage />} />
                <Route
                    path="/attendee/:id/edit"
                    element={<AttendeeFormPage />}
                />
                <Route path="/waivers" element={<Navigate replace to="/waiver-settings" />} />
                <Route
                    path="/waiver-settings"
                    element={<WaiverSettingsPage />}
                />
                <Route
                    path="/waiver-settings/new"
                    element={<WaiverSettingFormPage />}
                />
                <Route
                    path="/waiver-settings/:id/edit"
                    element={<WaiverSettingFormPage />}
                />
                <Route
                    path="/waivers/settings/new"
                    element={<WaiverSettingFormPage />}
                />
                <Route
                    path="/waivers/settings/:id/edit"
                    element={<WaiverSettingFormPage />}
                />
                <Route
                    path="/waiver-management"
                    element={<SignedWaiverManagementPage />}
                />
                <Route
                    path="/waiver-management/:id"
                    element={<SignedWaiverDetailsPage />}
                />
                <Route path="/stock" element={<StockPage />} />
                <Route path="/stock/new" element={<StockFormPage />} />
                <Route path="/stock/automation" element={<Navigate replace to="/purchase-orders" />} />
                <Route path="/purchase-orders" element={<StockAutomationPage />} />
                <Route path="/suppliers" element={<SuppliersPage />} />
                <Route path="/suppliers/new" element={<SupplierPage />} />
                <Route path="/suppliers/:id" element={<SupplierPage />} />
                <Route path="/stock/:id/edit" element={<StockFormPage />} />
                <Route path="/stock/inventory" element={<ManageInventoryPage />} />
                <Route path="/stock/inventory/scan" element={<InventoryScanPage />} />
                <Route path="/stock/inventory/stock-take" element={<StockTakePage />} />
                <Route path="/stock/inventory/stock-takes" element={<StockTakeHistoryPage />} />
                <Route path="/stock/inventory/movements" element={<StockMovementsPage />} />
                <Route path="/stock/inventory/purchase-order" element={<ReceivePurchaseOrderPage />} />
                <Route path="/stock/scan" element={<Navigate replace to="/stock/inventory/scan" />} />
                <Route
                    path="/stock/sub-categories"
                    element={<StockSubCategoriesPage />}
                />
                <Route
                    path="/stock/sub-categories/new"
                    element={<StockSubCategoryFormPage />}
                />
                <Route
                    path="/stock/sub-categories/:id/edit"
                    element={<StockSubCategoryFormPage />}
                />
                <Route path="/tables" element={<TableLayoutsPage />} />
                <Route path="/tables/new" element={<TableLayoutEditorPage />} />
                <Route path="/tables/:id" element={<TableLayoutEditorPage />} />
                <Route path="/staff" element={<StaffPage />} />
                <Route path="/staff/new" element={<StaffFormPage />} />
                <Route path="/staff/:id" element={<StaffFormPage view />} />
                <Route path="/staff/:id/edit" element={<StaffFormPage />} />
                <Route
                    path="/staff/:id/permissions"
                    element={<StaffPermissionsPage />}
                />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/subscription" element={<CrmSubscriptionPage />} />
                <Route path="/subscription/direct-debit/complete" element={<DirectDebitCompletePage />} />
                <Route
                    path="/subscription/detail"
                    element={<CrmSubscriptionPage />}
                />
                <Route
                    path="/subscription/edit"
                    element={<Navigate to="/subscription" replace />}
                />
                <Route path="/payments" element={<PaymentsPage />} />
                <Route path="/refunds" element={<RefundsPage />} />
                <Route path="/invoices" element={<InvoicesPage />} />
                <Route
                    path="/payment-gateway"
                    element={<PaymentGatewayPage />}
                />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/printer-log" element={<PrinterLogPage />} />
                <Route path="/invoices/credit-notes/:id" element={<CreditNoteViewPage />} />
                <Route path="/invoices/:id" element={<InvoiceViewPage />} />
                <Route path="/reviews" element={<ReviewsPage />} />
                <Route path="/admin/clients" element={<ClientsPage />} />
                <Route path="/admin/clients/new" element={<ClientFormPage />} />
                <Route
                    path="/admin/clients/:id"
                    element={<ClientDetailsPage />}
                />
                <Route
                    path="/admin/clients/:id/edit"
                    element={<ClientFormPage />}
                />
                <Route
                    path="/admin/clients/:id/subscription"
                    element={<ClientSubscriptionSetupPage />}
                />
                <Route
                    path="/admin/subscriptions"
                    element={<Navigate to="/admin/clients" replace />}
                />
                <Route
                    path="/admin/subscriptions/edit"
                    element={<Navigate to="/admin/clients" replace />}
                />
                <Route
                    path="/admin/subscription-requests"
                    element={<SubscriptionRequestsPage />}
                />
                <Route
                    path="/admin/subscription-requests/:id"
                    element={<SubscriptionRequestDetailsPage />}
                />
                <Route
                    path="/admin/user-subscriptions"
                    element={<Navigate to="/admin/subscription-requests" replace />}
                />
                <Route path="/admin/pages" element={<ContentPagesPage />} />
                <Route
                    path="/admin/pages/new"
                    element={<ContentPageFormPage />}
                />
                <Route
                    path="/admin/pages/:id"
                    element={<ContentPageDetailsPage />}
                />
                <Route
                    path="/admin/pages/:id/edit"
                    element={<ContentPageFormPage />}
                />
                <Route path="/admin/staff" element={<SuperAdminStaffPage />} />
                <Route
                    path="/admin/staff/new"
                    element={<SuperAdminStaffFormPage />}
                />
                <Route
                    path="/admin/staff/:id"
                    element={<SuperAdminStaffDetailsPage />}
                />
                <Route
                    path="/admin/staff/:id/edit"
                    element={<SuperAdminStaffFormPage />}
                />
                <Route
                    path="/admin/staff/:id/permissions"
                    element={<SuperAdminStaffPermissionsPage />}
                />
                <Route path="/admin/inquiries" element={<InquiriesPage />} />
                <Route
                    path="/admin/inquiries/:id"
                    element={<InquiryDetailsPage />}
                />
                <Route path="/admin/invoices" element={<AdminInvoicesPage />} />
                <Route path="/admin/invoices/layout" element={<InvoiceLayoutPage />} />
                <Route
                    path="/admin/invoices/:id"
                    element={<AdminInvoiceDetailsPage />}
                />
                <Route
                    path="/admin/email-templates"
                    element={<EmailTemplatesPage />}
                />
                <Route
                    path="/admin/email-templates/new"
                    element={<EmailTemplateFormPage />}
                />
                <Route
                    path="/admin/email-templates/:id"
                    element={<EmailTemplateDetailsPage />}
                />
                <Route
                    path="/admin/email-templates/:id/edit"
                    element={<EmailTemplateFormPage />}
                />
                <Route
                    path="/admin/payments"
                    element={<SuperAdminSecondaryPage page="payments" />}
                />
                <Route path="/admin/charge" element={<AdminChargePage />} />
                <Route path="/admin/smtp-settings" element={<PlatformSmtpPage />} />
                <Route path="/admin/ai-settings" element={<PlatformAiSettingsPage />} />
                <Route path="/admin/printer-log" element={<PrinterLogPage />} />
                <Route path="/admin/support-tickets" element={<SupportTicketsPage />} />
                <Route path="/admin/support-sessions" element={<SupportSessionsPage />} />
                <Route path="/admin/developers" element={<DevelopersPage />} />
                <Route
                    path="/admin/website-settings"
                    element={<WebsiteSettingsPage />}
                />
                <Route path="/pos" element={<PosPage />} />
                <Route path="/kiosk" element={<KioskPage />} />
                <Route path="/kds" element={<KdsPage />} />
                <Route path="/terminals" element={<TerminalsPage />} />
                <Route path="/printers" element={<PrintersPage />} />
                <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
        </LegacyShell>
    );
}
